// A promotion definition (FORT-310 engine). `config` is the type-specific blob the API stores as
// jsonb and returns as an object; cast it to the matching per-type shape based on `promotionType`.
// Only the Membership type is built now (FORT-315).

export const PROMOTION_TYPE_MEMBERSHIP = 'Membership';

// Config shape for a Membership promotion. `posItemName` is stored by name (clone-safe — per-client
// POS-item ids aren't portable); `memberCategoryId` is the numeric dbo.member_category id.
//
// `snapToBillingCycle` / `proRata` are FORT-319. Snap on → valid_to = (validFrom + durationMonths)
// snapped forward to the billing anchor; off → exactly validFrom + durationMonths. Pro-rata (only
// when snapping) charges the snapped extra period at the category's normal price on top of the
// fixed fee. Absent in older configs → treated as snap true / pro-rata false (no migration).
export interface MembershipPromotionConfig {
    memberCategoryId: number;
    fixedFee: number;
    durationMonths: number;
    posItemName: string;
    snapToBillingCycle: boolean;
    proRata: boolean;
}

export interface Promotion {
    id: number;
    name: string;
    promotionType: string;
    config: Record<string, unknown>;
    isActive: boolean;
    createdAt: Date;
    updatedAt: Date;
}

// Narrow a promotion's generic config to the Membership shape. Returns null when the promotion is a
// different type, so callers can render a type-specific view without unsafe casts.
export function asMembershipConfig(promotion: Promotion): MembershipPromotionConfig | null {
    if (promotion.promotionType !== PROMOTION_TYPE_MEMBERSHIP) {
        return null;
    }
    const config = promotion.config as Partial<MembershipPromotionConfig>;
    return {
        memberCategoryId: Number(config.memberCategoryId ?? 0),
        fixedFee: Number(config.fixedFee ?? 0),
        durationMonths: Number(config.durationMonths ?? 0),
        posItemName: String(config.posItemName ?? ''),
        // Absent flags → snap on / pro-rata off, matching the backend's null defaults.
        snapToBillingCycle: config.snapToBillingCycle ?? true,
        proRata: config.proRata ?? false,
    };
}
